import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { createSseParser } from '@thedealersconcierge/lib/cache/sse';
import { QueryClient } from '@tanstack/react-query';
import config from '~/config';
import { getAuthHeaders } from '~/lib/backend';
import { applyServerEvent } from './applyServerEvent';

/**
 * Open the customer SSE stream and dispatch each validated event to the cache.
 * Resolves when the stream ends; rejects on a connection error. Aborting
 * `signal` closes the connection. Frames are read defensively — a frame that is
 * not valid JSON or does not match the shared schema is skipped (lossy).
 *
 * We use fetch + a ReadableStream reader rather than the native EventSource API:
 * EventSource can't set request headers (only cookies), but the endpoint
 * authenticates with the customer's bearer JWT via getAuthHeaders().
 */
export const streamServerEvents = async (
  userId: string,
  queryClient: QueryClient,
  signal: AbortSignal
): Promise<void> => {
  const headers = new Headers(await getAuthHeaders());
  headers.set('Accept', 'text/event-stream');

  const url = `${config.rawBackendUrl}/events`;
  const response = await fetch(url, { method: 'GET', headers, signal });
  if (!response.ok || !response.body) {
    throw new Error(`SSE /events/customer responded ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }

    for (const payload of parser.push(
      decoder.decode(value, { stream: true })
    )) {
      let json: unknown;
      try {
        json = JSON.parse(payload);
      } catch {
        continue;
      }

      const parsed = ServerEventValidationSchema.safeParse(json);
      if (parsed.success) {
        applyServerEvent(queryClient, parsed.data);
      }
    }
  }
};
